import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as EventoDeTecla } from 'react';
import { api, dataBr, diaNaCasa, ErroDoPortal } from '../api';
import {
  GRUPOS_DE_SIMBOLO, NOME_DO_SIMBOLO, SO_LABORATORIO, desenhoDoSimbolo, simboloDaAmostra,
  simboloDoPonto, type EstadoSimbolo, type TipoSimbolo,
} from '../simbolosDoMapa';
// Só TIPOS do motor: o MapLibre é carregado sob demanda, no efeito do mapa
// (spec 05 §5.1) — uma importação comum o poria no pacote de todas as telas.
import type { ControleDoMapa, Falha, Feicao } from '../mapaGeotecnico';
import { NomeDaAmostra } from '../NomeDaAmostra';
import { aoMudarTemaEfetivo, temaEfetivo } from '../tema';

/**
 * O mapa da obra.
 *
 * ── Sobre o fundo cartográfico (onda 296, spec 05) ───────────────────
 *
 * Até a onda 296 as telhas de ruas vinham do OpenStreetMap: o navegador
 * do cliente pedia as telhas da região que estava olhando, e o serviço de
 * mapas ficava sabendo, aproximadamente, onde ficavam as obras
 * consultadas. Agora o fundo de ruas vem do NOSSO servidor (`/mapas/`, o
 * recorte do Brasil do Protomaps): o Portal deixou de consultar o
 * OpenStreetMap (spec §6.5). A única consulta externa que sobra é a do
 * satélite, à Esri, que recebe só a região olhada — nenhuma coordenada de
 * furo, nenhum número de OS. Os créditos (OpenStreetMap pelos dados,
 * Protomaps, Esri no satélite) ficam embaixo do mapa e não se removem.
 *
 * Sem o fundo — o arquivo fora do ar, ou a obra sem sinal —, o mapa fica
 * liso e OS PONTOS CONTINUAM APARECENDO, com a escala, e a tela avisa: na
 * obra, sem sinal, um croqui sem base ainda responde «onde ficou o SP-03
 * em relação ao SP-04».
 *
 * ── Por que MapLibre, e no pacote (onda 296, spec 05) ───────────────
 *
 * O §20 pede que o sistema não dependa de terceiro para funcionar: uma
 * biblioteca puxada de CDN é código de outra pessoa executando na página
 * do cliente, que muda sem aviso e some quando o CDN cai. O mapa era o
 * Leaflet, empacotado; agora é o MapLibre GL JS — aberto, sem chave e sem
 * conta —, que desenha em WebGL, também no pacote, com o *worker* e as
 * fontes dos rótulos servidos daqui (RN-MG3).
 *
 * E é o MESMO motor da bancada: a pasta `mapaGeotecnico/` é cópia idêntica,
 * byte a byte, da de lá (RN-MG1 — o teste 209 confere arquivo por
 * arquivo). Esta tela não fala com o MapLibre: monta os pontos e conversa
 * com o motor, que só é carregado quando o mapa abre. Sem WebGL (aparelho
 * antigo, aceleração desligada), a tela avisa e o motor mostra os pontos
 * numa lista dentro do quadro do mapa; a tabela embaixo continua.
 *
 * O motor sabe desenhar o acervo antigo da bancada, em azul. Aqui ele
 * NUNCA chega (RN-MG2): o acervo junta obras de todos os clientes. Os
 * pontos do Portal são amostras e pontos de investigação, e mais nada — o
 * teste 283.24 confere.
 *
 * ── Os fundos, e a busca por escrito (onda 181; o terceiro fundo, 296) ─
 *
 *   «aplique as mesmas opções de filtro no mapa que a Testesolo [tem] no
 *    Labmanager. Coloque a opção de visão satélite ou mapa também»
 *                                              — Testesolo, 11/09/2026
 *
 * Os filtros de obra, tipo e período já estavam aqui. O que faltava era
 * o que a bancada ganhou nas ondas 177 e 179: uma CAIXA DE BUSCA, porque
 * quem procura não lembra em que OS a obra está — lembra do nome dela,
 * ou da rua —, e a escolha do FUNDO.
 *
 * Os fundos são três desde a onda 296: Mapa (ruas), Satélite e Satélite
 * com ruas, no seletor do canto do mapa (do motor, o mesmo da bancada). O
 * satélite é a imagem «World Imagery» da Esri, que ela serve sem chave e
 * sem conta, só com o crédito no rodapé. É a mesma da bancada, e é o que o
 * §20 tolera: o Google Earth exigiria chave, cota e contrato. No escuro, o
 * fundo Mapa tem estilo escuro próprio; o satélite não muda.
 *
 * A escolha do fundo fica guardada no aparelho, na chave de sempre. É
 * conforto, não dado: se o navegador recusar, o mapa abre no fundo Mapa.
 *
 * ── Os símbolos, o agrupamento e a tela cheia (onda 220) ────────────
 *
 *   «Todas as melhorias no mapa que forem feitas para o LabManager,
 *    deverão ser implementadas para o Portal do Cliente. Isso é uma
 *    regra.»                                    — Humberto, 28/09/2026
 *
 * O que a onda 215 deu ao mapa da bancada, este ganha igual: o símbolo
 * diz o tipo (`simbolosDoMapa.ts` — CÓPIA do da bancada, e o teste 209
 * exige que as duas sejam idênticas, byte a byte); vazado é programado,
 * cheio é executado ou coletado; meia opacidade é coordenada estimada;
 * registros próximos viram um círculo com o número, que o zoom separa e
 * que, no mesmo ponto, o clique abre ao redor; passar o mouse diz o
 * nome; a faixa de tipos filtra e é a legenda; e há tela cheia. O
 * balão do clique continua — é o «mais informações» daqui, no lugar do
 * painel da bancada.
 */

/* A escolha do fundo, guardada no aparelho nesta chave (a de antes da
   onda 296); quem lê e grava é o motor. */
const CHAVE_FUNDO = 'testesolo.portal.mapa.fundo';

/** O lado do ícone no mapa do Portal, em px (a bancada usa 30). */
const TAMANHO_DO_ICONE = 28;

/** Sem acento e sem caixa — «sao» acha «São», «cacador» acha «Caçador». */
function chato(t: string | null | undefined): string {
  return (t ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** Blumenau, para quando não há ponto nenhum a enquadrar. [latitude, longitude]. */
const CENTRO_PADRAO: [number, number] = [-26.9194, -49.0661];

interface Ponto {
  tipo_ponto: 'sondagem' | 'amostra';
  ponto_id: string;
  os_id: string;
  os_numero: string;
  obra_codigo: string;
  obra_nome: string;
  codigo: string;
  subtipo: string;
  subtipo_nome: string;
  latitude: number;
  longitude: number;
  origem_coordenada: string;
  data: string | null;
  cota: number | null;
  profundidade: number | null;
  nivel_agua: number | null;
  situacao: string;
  detalhe: string | null;
  documentos: string;
  endereco: string | null;
  municipio: string | null;
  uf: string | null;
  /* 0204 · o símbolo da amostra (220). */
  acondicionamento: string | null;
  amostra_origem: string | null;
}

interface Obra {
  os_id: string; os_numero: string; obra_nome: string;
  pontos: string; primeira: string | null; ultima: string | null;
  endereco: string | null; municipio: string | null; uf: string | null;
}
interface Tipo { subtipo: string; subtipo_nome: string; pontos: string }
interface Cidade { municipio: string; uf: string | null; pontos: string }

interface Resposta {
  pontos: Ponto[];
  filtros: {
    obras: Obra[];
    tipos: Tipo[];
    cidades: Cidade[];
    periodo: { de: string | null; ate: string | null };
  };
}

const soODia = (v: string | null) => diaNaCasa(v);
const numeroBr = (n: number) => String(n).replace('.', ',');

/** O símbolo de um ponto do portal (220) — as mesmas regras da bancada. */
function simboloDe(p: Ponto): { tipo: TipoSimbolo; estado: EstadoSimbolo } {
  if (p.tipo_ponto === 'sondagem') {
    return { tipo: simboloDoPonto(p.subtipo),
      estado: p.situacao === 'Executado' ? 'exec' : 'prog' };
  }
  const tipo = simboloDaAmostra({ origem: p.amostra_origem,
    acondicionamento: p.acondicionamento, tipoAmostra: p.subtipo });
  /* A coleta aparece cheia: foi coletada. «Só laboratório» fica cheio
     quando já há documento entregue sobre a amostra. */
  const estado: EstadoSimbolo = !SO_LABORATORIO.includes(tipo) || Number(p.documentos) > 0 ? 'exec' : 'prog';
  return { tipo, estado };
}

/** A coordenada não foi medida: meia opacidade no mapa (RN-MG6). */
const coordenadaEstimada = (p: Ponto) => /estimad|projeto/i.test(p.origem_coordenada);

/** O nome que o cliente usa: a identificação da amostra, ou o código. */
const nomeDoPonto = (p: Ponto) => (p.tipo_ponto === 'amostra' && p.detalhe ? p.detalhe : p.codigo);

/**
 * O código AM, quando o nome do ponto é o do cliente (onda 303, RN-248):
 * a amostra aparece pelo nome que o cliente deu, e o código único logo
 * depois, menor. Vazio quando o nome já é o código.
 */
const codigoDeApoio = (p: Ponto) => (nomeDoPonto(p) !== p.codigo ? p.codigo : '');

/** A etiqueta de passar o mouse: o nome que o cliente usa, o código AM, e a obra. */
function dica(p: Ponto): string {
  const nome = nomeDoPonto(p);
  const obra = p.obra_nome.length > 28 ? `${p.obra_nome.slice(0, 27)}…` : p.obra_nome;
  const cod = codigoDeApoio(p);
  return `<b>${escapar(nome)}</b><br><small>${cod ? `${escapar(cod)} · ` : ''}${
    escapar(obra)} · ${escapar(NOME_DO_SIMBOLO[simboloDe(p).tipo])}</small>`;
}

const escapar = (t: string) => t.replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c));

function balao(p: Ponto): string {
  /* Onda 303 (RN-248): a amostra pelo nome do cliente, o código AM na
     linha de baixo; o furo, pelo código dele, como sempre. */
  const cod = codigoDeApoio(p);
  const linhas: string[] = [
    `<strong>${escapar(nomeDoPonto(p))}</strong>`,
    ...(cod ? [`<span class="mono">${escapar(cod)}</span>`] : []),
    `${escapar(p.subtipo_nome)} — ${escapar(p.situacao)}`,
    `OS ${escapar(p.os_numero)}`,
  ];
  const onde = [p.endereco, p.municipio && `${p.municipio}${p.uf ? `/${p.uf}` : ''}`]
    .filter(Boolean).join(' · ');
  if (onde) linhas.push(escapar(onde));
  if (p.data) linhas.push(`Data: ${dataBr(p.data)}`);
  if (p.profundidade != null) linhas.push(`Profundidade: ${numeroBr(p.profundidade)} m`);
  if (p.nivel_agua != null) linhas.push(`Nível d'água: ${numeroBr(p.nivel_agua)} m`);
  if (p.cota != null) linhas.push(`Cota: ${numeroBr(p.cota)} m`);
  /* Na amostra, o `detalhe` é o nome do cliente — já está no título. */
  if (p.detalhe && !cod) linhas.push(escapar(p.detalhe));
  /*
   * A origem da coordenada aparece SEMPRE, e por extenso.
   *
   * Um ponto estimado desenhado igual a um levantado faz o mapa afirmar
   * uma exatidão que ninguém mediu — e é o tipo de coisa que vira
   * discussão em obra dois anos depois.
   */
  linhas.push(`<em>Coordenada: ${escapar(p.origem_coordenada)}</em>`);
  return linhas.join('<br>');
}

/**
 * O ponto, como o motor o quer (spec 05 §5.2). A sondagem é `ponto`, a
 * amostra é `amostra` — e só: o Portal nunca envia `acervo` (RN-MG2). O
 * `lugar` é o próprio ponto (o balão é de um ponto só), e o `cliente` da
 * feição leva a OBRA, que é o que a dica daqui diz embaixo do nome.
 */
function feicaoDe(p: Ponto): Feicao {
  const { tipo, estado } = simboloDe(p);
  return {
    chave: `${p.tipo_ponto}:${p.ponto_id}`,
    lugar: p.ponto_id,
    tipo,
    estado,
    fonte: p.tipo_ponto === 'sondagem' ? 'ponto' : 'amostra',
    estimada: coordenadaEstimada(p),
    /* Onda 303 (RN-248): «SP-01/2 · AM-2026-00202» — o nome do cliente e
       o código AM, para a lista do leitor de tela dizer os dois. */
    nome: [nomeDoPonto(p), codigoDeApoio(p)].filter(Boolean).join(' · '),
    cliente: p.obra_nome,
    latitude: p.latitude,
    longitude: p.longitude,
  };
}

/**
 * O mesmo, em texto, para a lista que o leitor de tela lê (spec 05 §4.6):
 * nome · tipo (programada | executada) · obra — o tipo e o estado, como na
 * bancada. Texto puro — o motor o põe com `textContent`.
 */
const textoDaLista = (f: Feicao) =>
  `${f.nome} · ${NOME_DO_SIMBOLO[f.tipo]} (${f.estado === 'exec' ? 'executada' : 'programada'}) · ${f.cliente}`;

export function Mapa() {
  const [dados, setDados] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [os, setOs] = useState<string | null>(null);
  const [tipo, setTipo] = useState<string | null>(null);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [busca, setBusca] = useState('');
  const [cidade, setCidade] = useState<string | null>(null);
  /* 220 · filtro por símbolo (a faixa que é também a legenda) e tela cheia. */
  const [simbolos, setSimbolos] = useState<Set<TipoSimbolo>>(new Set());
  const [cheia, setCheia] = useState(false);
  /* O ponto escolhido no mapa (o halo, spec 05 §5.3); o balão é do motor. */
  const [selecionado, setSelecionado] = useState<string | null>(null);
  /* O que o motor avisou que falhou (spec 05 §5.7): o fundo, o satélite,
     o WebGL, o próprio motor. Os pontos continuam em todos os casos. */
  const [falhas, setFalhas] = useState<ReadonlySet<Falha>>(() => new Set());
  const [motorAusente, setMotorAusente] = useState(false);

  const raiz = useRef<HTMLDivElement>(null);
  const div = useRef<HTMLDivElement>(null);
  /* O motor do mapa (spec 05 §5.1): nasce uma vez, no efeito abaixo; o
     estado faz os efeitos dos dados e da seleção esperarem por ele. */
  const [controle, setControle] = useState<ControleDoMapa | null>(null);
  /* Os pontos da tela agora, e os que o motor já recebeu — para não os
     mandar duas vezes quando ele nasce com os dados já na mão, e para a
     dica e o balão acharem o ponto do símbolo que o motor desenhou. */
  const pontosAtuais = useRef<Ponto[]>([]);
  const pontosNoMotor = useRef<Ponto[] | null>(null);
  const pontoDoLugar = useRef(new Map<string, Ponto>());

  const carregar = useCallback((sinal: AbortSignal) => {
    const q = new URLSearchParams();
    if (os) q.set('os', os);
    if (tipo) q.set('tipo', tipo);
    if (de) q.set('de', de);
    if (ate) q.set('ate', ate);
    if (busca) q.set('busca', busca);
    if (cidade) q.set('municipio', cidade);
    api<Resposta>(`/mapa?${q}`, { sinal })
      .then((r) => { setDados(r); setErro(null); })
      .catch((e) => {
        if ((e as Error)?.name === 'AbortError') return;
        setErro(e instanceof ErroDoPortal ? e.message : 'Não foi possível carregar.');
      });
  }, [os, tipo, de, ate, busca, cidade]);

  useEffect(() => {
    const corte = new AbortController();
    carregar(corte.signal);
    return () => corte.abort();
  }, [carregar]);

  /* 220 · o filtro por símbolo é da tela: não vai ao servidor. */
  const porSimbolo = useMemo(() => {
    const n = new Map<TipoSimbolo, number>();
    for (const p of dados?.pontos ?? []) {
      const t = simboloDe(p).tipo; n.set(t, (n.get(t) ?? 0) + 1);
    }
    return n;
  }, [dados]);
  const visiveis = useMemo(() => {
    const todos = dados?.pontos ?? [];
    return simbolos.size ? todos.filter((p) => simbolos.has(simboloDe(p).tipo)) : todos;
  }, [dados, simbolos]);
  pontosAtuais.current = visiveis;
  const alternarSimbolo = (t: TipoSimbolo | null) => setSimbolos((atual) => {
    if (!t) return new Set();
    const novo = new Set(atual);
    if (novo.has(t)) novo.delete(t); else novo.add(t);
    return novo;
  });

  /*
   * O mapa nasce uma vez. O motor vem SOB DEMANDA (o MapLibre pesa, e só
   * esta tela o usa); enquanto ele chega, os dados já podem ter chegado —
   * entram assim que ele nasce, e as cargas seguintes, pelo efeito dos
   * dados. Sem WebGL, ou com um erro dentro do motor, o próprio motor
   * mostra a lista e avisa por `aoFalhar('webgl')` ou `aoFalhar('motor')`.
   */
  useEffect(() => {
    let vivo = true;
    let criado: ControleDoMapa | null = null;
    let desligarTema: (() => void) | null = null;
    void (async () => {
      const { criarMapa } = await import('../mapaGeotecnico');
      if (!vivo || !div.current) return;
      const c = await criarMapa(div.current, {
        casa: CENTRO_PADRAO,
        chaveDoFundo: CHAVE_FUNDO,
        chaveDasCamadas: 'testesolo.portal.mapa.camadas',
        tema: temaEfetivo(),
        tamanhoDoIcone: TAMANHO_DO_ICONE,
        dica: (f) => {
          const p = pontoDoLugar.current.get(f.lugar);
          return p ? dica(p) : `<b>${escapar(f.nome)}</b>`;
        },
        textoDaLista,
        balao: (lugar) => {
          const p = pontoDoLugar.current.get(lugar);
          return p ? balao(p) : null;
        },
      });
      if (!vivo) { c.destruir(); return; }
      criado = c;
      /* O motor pesa ~1 MB e a resposta da API, não: os pontos costumam
         chegar ANTES dele. Entram aqui mesmo, antes de o estilo do mapa
         carregar — o motor os guarda e os desenha quando ele ficar pronto. */
      entregar(c, pontosAtuais.current);
      c.aoEscolher((lugar) => setSelecionado(lugar));
      /* O clique no fundo fecha o balão (é o padrão dele); o halo vai junto. */
      c.aoClicarNoMapa(() => setSelecionado(null));
      c.aoFalhar((f, falhou) => setFalhas((antes) => {
        if (antes.has(f) === falhou) return antes;
        const novo = new Set(antes);
        if (falhou) novo.add(f); else novo.delete(f);
        return novo;
      }));
      desligarTema = aoMudarTemaEfetivo((t) => c.definirTema(t));
      setControle(c);
    })().catch((e) => {
      // O pedaço do motor não veio (rede, versão nova publicada no meio):
      // a tabela embaixo continua; a tela diz que o mapa não abriu.
      console.error('mapa: o motor não carregou', e);
      if (vivo) setMotorAusente(true);
    });
    return () => {
      vivo = false;
      desligarTema?.();
      criado?.destruir();
      pontosNoMotor.current = null;
      setControle(null);
    };
  }, []);

  /** Entrega os pontos ao motor e enquadra o que há (sem ponto, a casa; um só, zoom de bairro). */
  function entregar(c: ControleDoMapa, pontos: Ponto[]) {
    pontoDoLugar.current = new Map(pontos.map((p) => [p.ponto_id, p]));
    c.definirRegistros(pontos.map(feicaoDe));
    c.enquadrar();
    pontosNoMotor.current = pontos;
  }

  /*
   * Os símbolos: refeitos quando os DADOS mudam, não quando a seleção muda
   * — refazer fecharia o círculo que a pessoa acabou de abrir.
   */
  useEffect(() => {
    if (!controle || pontosNoMotor.current === visiveis) return;
    entregar(controle, visiveis);
  }, [controle, visiveis]);

  /* Outro filtro, outros pontos: o escolhido deixa de valer. */
  useEffect(() => { setSelecionado(null); }, [visiveis]);

  /* A seleção (o halo), e o mapa vai até o ponto. */
  useEffect(() => {
    controle?.selecionar(selecionado);
  }, [controle, selecionado]);

  /*
   * A tela cheia (220): a Fullscreen API quando o navegador deixa; senão
   * a janela inteira por CSS. Esc sai nas duas. O mapa mede o quadro ao
   * nascer — depois de mudar o tamanho, `redimensionar()`.
   */
  const alternarCheia = useCallback(async () => {
    const el = raiz.current;
    if (!el) return;
    if (document.fullscreenElement) { await document.exitFullscreen().catch(() => {}); return; }
    if (cheia) { setCheia(false); return; }
    if (typeof el.requestFullscreen === 'function') {
      try { await el.requestFullscreen(); return; } catch { /* cai no CSS */ }
    }
    setCheia(true);
  }, [cheia]);
  useEffect(() => {
    const mudou = () => setCheia(!!raiz.current && document.fullscreenElement === raiz.current);
    document.addEventListener('fullscreenchange', mudou);
    return () => document.removeEventListener('fullscreenchange', mudou);
  }, []);
  useEffect(() => {
    if (!cheia) return undefined;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.fullscreenElement) setCheia(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [cheia]);
  useEffect(() => {
    const t = setTimeout(() => controle?.redimensionar(), 80);
    return () => clearTimeout(t);
  }, [cheia, controle]);

  const contagem = useMemo(() => {
    const p = visiveis;
    return {
      total: p.length,
      sondagens: p.filter((x) => x.tipo_ponto === 'sondagem').length,
      amostras: p.filter((x) => x.tipo_ponto === 'amostra').length,
    };
  }, [visiveis]);

  const filtrando = Boolean(os || tipo || de || ate || busca || cidade || simbolos.size);
  const periodo = dados?.filtros.periodo;

  return (
    <div className={`mapa-tela${cheia ? ' tela-cheia' : ''}`} ref={raiz}>
      <div className="mapa-cabeca">
        <div>
          <h1>Mapa</h1>
          <p className="linha-obra">
            Onde cada sondagem foi executada e onde cada amostra com coordenada
            foi coletada.
          </p>
        </div>
        <button type="button" className="mapa-tela-cheia" aria-pressed={cheia}
                data-icone={cheia ? '⤡' : '⛶'} onClick={() => { void alternarCheia(); }}>
          {cheia ? 'Sair da tela cheia' : 'Tela cheia'}
        </button>
      </div>

      {erro && <div className="aviso ruim" role="alert">{erro}</div>}

      {/*
        A busca vem ANTES das fichas, e não depois, porque é a primeira
        pergunta de quem chega: «onde está a obra da rua tal». As fichas
        são o ajuste que se faz DEPOIS de achar.
      */}
      <Busca
        valor={busca}
        obras={dados?.filtros.obras ?? []}
        cidades={dados?.filtros.cidades ?? []}
        tipos={dados?.filtros.tipos ?? []}
        aoEscrever={setBusca}
        aoEscolher={(alvo) => {
          setBusca('');
          if (alvo.chave === 'os') { setOs(alvo.valor); setCidade(null); }
          else if (alvo.chave === 'municipio') { setCidade(alvo.valor); setOs(null); }
          else setTipo(alvo.valor);
        }}
      />

      <div className="secao"><h2>Filtros</h2></div>

      <div className="filtros filtros-seletores">
        <Seletor
          rotulo="Obra" valor={os} todos="Todas as obras"
          opcoes={(dados?.filtros.obras ?? []).map((o) => ({
            valor: o.os_id,
            texto: `${o.obra_nome} (${o.pontos})`,
            titulo: [o.endereco, o.municipio].filter(Boolean).join(' · ') || undefined,
          }))}
          aoMudar={setOs} />
        <Seletor
          rotulo="Cidade" valor={cidade} todos="Todas as cidades"
          opcoes={(dados?.filtros.cidades ?? []).map((c) => ({
            valor: c.municipio,
            texto: `${c.uf ? `${c.municipio}/${c.uf}` : c.municipio} (${c.pontos})`,
          }))}
          aoMudar={setCidade} />
        <Seletor
          rotulo="Tipo" valor={tipo} todos="Todos os tipos"
          opcoes={(dados?.filtros.tipos ?? []).map((t) => ({
            valor: t.subtipo, texto: `${t.subtipo_nome} (${t.pontos})`,
          }))}
          aoMudar={setTipo} />

        {/*
          As duas datas andam juntas, sob um rótulo só. Separadas, «De» e
          «Até» viravam duas colunas do tamanho de um seletor de obra e a
          segunda caía sozinha na linha de baixo.
        */}
        <div className="campo filtro-seletor filtro-periodo">
          <span className="rot">Período</span>
          <div className="par-de-datas">
            <input type="date" value={de} aria-label="Data inicial"
                   min={soODia(periodo?.de ?? null)}
                   max={soODia(periodo?.ate ?? null)}
                   onChange={(e) => setDe(e.currentTarget.value)} />
            <input type="date" value={ate} aria-label="Data final"
                   min={soODia(periodo?.de ?? null)}
                   max={soODia(periodo?.ate ?? null)}
                   onChange={(e) => setAte(e.currentTarget.value)} />
          </div>
        </div>

        {filtrando && (
          <button type="button" className="limpar"
                  onClick={() => {
                    setOs(null); setTipo(null); setDe(''); setAte('');
                    setBusca(''); setCidade(null); setSimbolos(new Set());
                  }}>
            limpar filtros
          </button>
        )}
      </div>

      <FiltroDeSimbolos marcados={simbolos} contagem={porSimbolo} aoAlternar={alternarSimbolo} />

      <p className="sub" style={{ marginTop: '0.6rem' }} aria-live="polite">
        {contagem.total === 0
          ? 'Nenhum ponto com coordenada para este filtro.'
          : `${contagem.total} ponto(s): ${contagem.sondagens} sondagem(ns) `
            + `e ${contagem.amostras} amostra(s)`}
      </p>

      {/* Spec 05 §5.7: o que falhou no mapa — e os pontos continuam. */}
      {falhas.has('webgl') && (
        <div className="aviso mapa-aviso" role="status">
          <strong>O mapa não abriu.</strong>{' '}
          Este navegador não desenha o mapa (WebGL desligado). Os registros estão na lista abaixo.
        </div>
      )}
      {falhas.has('motor') && (
        <div className="aviso mapa-aviso" role="status">
          <strong>O mapa não abriu.</strong>{' '}
          Um erro dentro do mapa impediu o desenho nesta página. Os registros estão na lista abaixo.
        </div>
      )}
      {motorAusente && (
        <div className="aviso mapa-aviso" role="status">
          <strong>O mapa não abriu.</strong>{' '}
          O mapa não carregou nesta página. Os pontos continuam na tabela abaixo; recarregue para tentar de novo.
        </div>
      )}
      {/* Um aviso por fonte do fundo: no «Satélite com ruas» as duas podem
          faltar, e trocar de fundo tira o aviso da que saiu (o motor zera). */}
      {falhas.has('fundo') && (
        <div className="aviso mapa-aviso" role="status">
          <strong>O fundo do mapa não carregou.</strong>{' '}
          Os pontos estão no lugar; o que falta é o mapa de ruas. Sem ele o
          mapa fica liso.
        </div>
      )}
      {falhas.has('satelite') && (
        <div className="aviso mapa-aviso" role="status">
          <strong>A imagem de satélite não carregou.</strong>{' '}
          Os pontos estão no lugar; o que falta é a imagem de satélite. Sem
          ela o mapa fica liso.
        </div>
      )}

      <div ref={div} className="mapa" role="application"
           aria-label="Mapa dos pontos de investigação" />

      <div className="legenda">
        <span><span className="alfinete simbolo legenda"
          dangerouslySetInnerHTML={{ __html: desenhoDoSimbolo('sp', 'prog') }} /> vazado — programado</span>
        <span><span className="alfinete simbolo legenda"
          dangerouslySetInnerHTML={{ __html: desenhoDoSimbolo('sp', 'exec') }} /> cheio — executado ou coletado</span>
        <span><span className="alfinete simbolo legenda estimada"
          dangerouslySetInnerHTML={{ __html: desenhoDoSimbolo('sp', 'exec') }} /> transparente — coordenada estimada</span>
        <span><span className="mapa-cluster p legenda"><b>3</b></span> pontos próximos — toque para abrir</span>
      </div>

      {/*
        A lista embaixo do mapa não é redundância: é o que funciona no
        celular com sinal ruim, no leitor de tela e na impressão — três
        situações em que o mapa não responde e a pergunta continua sendo
        «quais furos foram feitos e quando».
      */}
      {contagem.total > 0 && (
        <>
          <div className="secao"><h2>Pontos</h2></div>
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">Ponto</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Data</th>
                  <th scope="col">Profundidade</th>
                  <th scope="col">Coordenada</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((p) => (
                  <tr key={`${p.tipo_ponto}:${p.ponto_id}`}>
                    {/* Onda 303 (RN-248): a amostra pelo nome do cliente e o
                        código AM embaixo; o furo, pelo código. */}
                    <th scope="row" className={p.tipo_ponto === 'amostra' ? undefined : 'mono'}>
                      {p.tipo_ponto === 'amostra'
                        ? <NomeDaAmostra amostra={{ codigo: p.codigo, identificacao: p.detalhe }} />
                        : p.codigo}
                      <span className="sub">{p.obra_nome}</span>
                    </th>
                    <td>
                      {p.subtipo_nome}
                      <span className="sub">{p.situacao}</span>
                    </td>
                    <td className="mono">{dataBr(p.data)}</td>
                    <td className="mono">
                      {p.profundidade != null
                        ? `${numeroBr(p.profundidade)} m`
                        : <span className="nada">—</span>}
                    </td>
                    <td className="mono">
                      {p.latitude.toFixed(6).replace('.', ',')},{' '}
                      {p.longitude.toFixed(6).replace('.', ',')}
                      <span className="sub">{p.origem_coordenada}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A faixa de tipos (220) — os mesmos símbolos do mapa, com a contagem, e
 * a mesma regra da bancada: nenhum marcado mostra todos; marcar mostra só
 * os marcados. Só aparecem os tipos que existem nas obras do cliente:
 * aqui, oferecer «Vane» a quem nunca teve um é caminho para o vazio.
 */
function FiltroDeSimbolos({ marcados, contagem, aoAlternar }: {
  marcados: Set<TipoSimbolo>;
  contagem: Map<TipoSimbolo, number>;
  aoAlternar(t: TipoSimbolo | null): void;
}) {
  const grupos = GRUPOS_DE_SIMBOLO
    .map((g) => ({ ...g, tipos: g.tipos.filter((t) => (contagem.get(t.tipo) ?? 0) > 0 || marcados.has(t.tipo)) }))
    .filter((g) => g.tipos.length > 0);
  if (!grupos.length) return null;
  return (
    <div className="mapa-tipos" role="group" aria-label="Tipos no mapa">
      {grupos.map((g) => (
        <div key={g.grupo} className="mapa-tipos-grupo">
          <span className="rot">{g.grupo}</span>
          {g.tipos.map(({ tipo, nome }) => {
            const marcado = marcados.has(tipo);
            return (
              <button key={tipo} type="button"
                className={`chip-tipo t-${tipo}${marcados.size && !marcado ? ' apagado' : ''}`}
                aria-pressed={marcado} onClick={() => aoAlternar(tipo)}>
                <span className="simbolo-do-chip"
                  dangerouslySetInnerHTML={{ __html: desenhoDoSimbolo(tipo, 'exec') }} />
                <span>{nome}</span>
                <small>{contagem.get(tipo) ?? 0}</small>
              </button>
            );
          })}
        </div>
      ))}
      {marcados.size > 0 && (
        <button type="button" className="limpar" onClick={() => aoAlternar(null)}>
          mostrar todos os tipos
        </button>
      )}
    </div>
  );
}

/**
 * Um filtro em lista suspensa.
 *
 * ── Por que `<select>` nativo, e não fichas ──────────────────────────
 *
 *   «coloque filtros dropdown, e não vários itens listados»
 *                                              — Testesolo, 11/09/2026
 *
 * É a mesma decisão que a bancada tomou na onda 150, pelo mesmo motivo:
 * uma fileira de fichas cresce com o cadastro. Com três obras cabe; com
 * trinta, ela estoura a largura do celular e empurra o mapa para baixo
 * da dobra — e o mapa é o que a pessoa veio ver.
 *
 * Nativo, e não um componente próprio: no celular o sistema abre a
 * roleta, com alvos do tamanho do dedo e busca por letra de graça.
 *
 * A contagem vai no texto da opção — «Edifício Mirante (4)» — porque era
 * o que a ficha mostrava ao lado do nome, e é o que diz de antemão se
 * vale a pena escolher.
 */
function Seletor({ rotulo, valor, opcoes, aoMudar, todos }: {
  rotulo: string;
  valor: string | null;
  opcoes: { valor: string; texto: string; titulo?: string }[];
  aoMudar(v: string | null): void;
  todos: string;
}) {
  /* Sem opção nenhuma o seletor não aparece: uma lista suspensa vazia é
     um controle que promete e não entrega. */
  if (opcoes.length === 0) return null;
  return (
    <label className="campo filtro-seletor">
      <span className="rot">{rotulo}</span>
      <select value={valor ?? ''}
              onChange={(e) => aoMudar(e.currentTarget.value || null)}>
        <option value="">{todos}</option>
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor} title={o.titulo}>{o.texto}</option>
        ))}
      </select>
    </label>
  );
}

/* ── A caixa de busca ───────────────────────────────────────────────── */

interface Sugestao {
  grupo: string;
  chave: 'os' | 'municipio' | 'tipo';
  valor: string;
  texto: string;
  detalhe?: string;
}

const GRUPOS = ['Obras', 'Cidades', 'Ensaios e sondagens'];

/**
 * O que a caixa sugere para o que foi escrito.
 *
 * As sugestões saem do que EXISTE nas obras deste cliente, e não de um
 * catálogo: oferecer «SCPTu» a quem nunca teve um é um caminho que só
 * leva a «nenhum resultado», e quem clica conclui que o sistema errou.
 *
 * No máximo cinco por grupo. Quem tem mais de cinco obras com «pre»
 * escreve mais uma letra — e uma lista que passa da altura da tela, no
 * celular, deixa de ser ajuda.
 */
function sugerir(
  obras: Obra[], cidades: Cidade[], tipos: Tipo[], texto: string,
): Sugestao[] {
  const q = chato(texto.trim());
  if (q.length < 1) return [];
  const bate = (...campos: (string | null | undefined)[]) =>
    campos.some((c) => chato(c).includes(q));

  const tudo: Sugestao[] = [];
  for (const o of obras) {
    if (!bate(o.obra_nome, o.os_numero, o.endereco, o.municipio)) continue;
    const onde = [o.endereco, o.municipio && `${o.municipio}${o.uf ? `/${o.uf}` : ''}`]
      .filter(Boolean).join(' · ');
    tudo.push({
      grupo: 'Obras', chave: 'os', valor: o.os_id, texto: o.obra_nome,
      detalhe: [`OS ${o.os_numero}`, onde].filter(Boolean).join(' · '),
    });
  }
  for (const c of cidades) {
    if (!bate(c.municipio, c.uf)) continue;
    tudo.push({
      grupo: 'Cidades', chave: 'municipio', valor: c.municipio,
      texto: c.uf ? `${c.municipio}/${c.uf}` : c.municipio,
      detalhe: `${c.pontos} ponto(s)`,
    });
  }
  for (const t of tipos) {
    if (!bate(t.subtipo_nome, t.subtipo)) continue;
    tudo.push({
      grupo: 'Ensaios e sondagens', chave: 'tipo', valor: t.subtipo,
      texto: t.subtipo_nome, detalhe: `${t.pontos} ponto(s)`,
    });
  }

  const porGrupo = new Map<string, Sugestao[]>();
  for (const x of tudo) {
    const lista = porGrupo.get(x.grupo) ?? [];
    if (lista.length < 5) lista.push(x);
    porGrupo.set(x.grupo, lista);
  }
  return GRUPOS.flatMap((g) => porGrupo.get(g) ?? []);
}

/**
 * Um `combobox` de verdade: seta para baixo desce, Enter escolhe, Esc
 * fecha. Não é capricho — no celular a lista aparece sobre o teclado, e
 * quem está de luva na obra precisa acertar de primeira.
 *
 * Escrever e NÃO escolher nada também vale: o texto vira filtro, e o
 * servidor procura pelo mesmo conjunto de campos. É o caso de quem
 * digita o código do furo, que não está em sugestão nenhuma.
 */
function Busca({ valor, obras, cidades, tipos, aoEscrever, aoEscolher }: {
  valor: string;
  obras: Obra[];
  cidades: Cidade[];
  tipos: Tipo[];
  aoEscrever(v: string): void;
  aoEscolher(s: Sugestao): void;
}) {
  const [texto, setTexto] = useState(valor);
  const [aberta, setAberta] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const raiz = useRef<HTMLDivElement>(null);

  useEffect(() => { setTexto(valor); }, [valor]);

  /* Clique fora fecha a lista — senão ela fica pendurada sobre o mapa. */
  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberta(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  const sugestoes = useMemo(
    () => sugerir(obras, cidades, tipos, texto), [obras, cidades, tipos, texto]);

  const escolher = (s: Sugestao) => {
    setTexto('');
    setAberta(false);
    aoEscolher(s);
  };

  const tecla = (e: EventoDeTecla<HTMLInputElement>) => {
    if (e.key === 'Escape') { setAberta(false); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!sugestoes.length) return;
      setAberta(true);
      setAtivo((i) => {
        const n = sugestoes.length;
        return e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n;
      });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (aberta && sugestoes[ativo]) escolher(sugestoes[ativo]);
      else { aoEscrever(texto.trim()); setAberta(false); }
    }
  };

  let anterior = '';
  return (
    <div className="mapa-busca" ref={raiz}>
      <label className="campo" style={{ marginBottom: 0 }}>
        <span className="rot">Procurar</span>
        <input
          type="search"
          role="combobox"
          aria-expanded={aberta && sugestoes.length > 0}
          aria-autocomplete="list"
          placeholder="nome da obra, rua, cidade, OS ou código do furo"
          value={texto}
          onChange={(e) => {
            setTexto(e.currentTarget.value);
            setAtivo(0);
            setAberta(true);
          }}
          onFocus={() => setAberta(true)}
          onKeyDown={tecla}
        />
      </label>

      {aberta && sugestoes.length > 0 && (
        <ul className="mapa-sugestoes" role="listbox">
          {sugestoes.map((s, i) => {
            const cabeca = s.grupo !== anterior ? s.grupo : null;
            anterior = s.grupo;
            return (
              <li key={`${s.chave}:${s.valor}`}>
                {cabeca && <div className="mapa-sugestao-grupo">{cabeca}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={i === ativo}
                  className={i === ativo ? 'mapa-sugestao atual' : 'mapa-sugestao'}
                  onMouseEnter={() => setAtivo(i)}
                  onClick={() => escolher(s)}
                >
                  <span>{s.texto}</span>
                  {s.detalhe && <span className="sub">{s.detalhe}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
